import { Chatbot, Data, Modal, SubscriptionPlan, UI, Utils } from 'common';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import isArray from 'lodash/isArray';
import isNumber from 'lodash/isNumber';
import PropTypes from 'prop-types';
import React from 'react';
import CreateContainerLabelModal from './containerLabel/CreateContainerLabelModal.component';
import ChangeCostCodeModal from './costCodes/ChangeCostCodeModal.component';
import CreateDHLeCModal from './dhlec/CreateDHLeCModal.component';
import ReprintDHLeCModal from './dhlec/ReprintDHLeCModal.component';
import ManagePickupsModal from './pickup/ManagePickupsModal.component';
import PickupErrorModal from './pickup/PickupErrorModal.component';
import ScheduleCarrierPickupModal from './pickup/ScheduleCarrierPickupModal.component';
import ScheduleUspsPickupModal from './pickup/ScheduleUspsPickupModal.component';
import ErrorRefundModal from './refund/ErrorRefundModal.component';
import PartialEligibilityWarningModal from './refund/PartialEligibilityWarningModal.component';
import RefundModal from './refund/RefundModal.component';
import CreateScanFormModal from './scanForm/CreateScanFormModal.component';
import ReprintScanFormModal from './scanForm/ReprintScanFormModal.component';
import { EligibleFor, EligibleForParams } from './shared/EligibleForOptions';
import HistoryCommunication from './utils/HistoryCommunication';

const { WrappedToolbar, WrappedButtonType } = UI;
const { Session, Util, BillingPlans, Analytics } = Utils;
const { ModalContext, ModalButtonActionType } = Modal;
const { Carrier, Capability, InternalCapability, PlanIds } = Data;
const { UpgradePlanModal, getUpgradeIconProps } = SubscriptionPlan;
const { ChatbotTriggers, launchChatbot } = Chatbot;

class Toolbar extends React.Component {
	constructor(props) {
		super(props);

		const { ShowUpsPickup, ShowDhlPickup } = Session.getSettings();
		const accountInfo = Session.getAccountInfo();
		const { userId } = accountInfo;
		const configuredCarriers = isArray(accountInfo.configuredCarriers)
			? accountInfo.configuredCarriers
			: [];

		// show "USPS & GlobalPost" option if true, otherwise, show "USPS" option
		const showUspsAndGlobalOption =
			Session.hasCapability(Capability.Globalpost) ||
			Session.hasCapability(Capability.GlobalPostPlus);

		// show UPS when the ShowUpsPickup is true and UPS is configured
		const showUpsOption =
			ShowUpsPickup &&
			configuredCarriers.some(
				config => config.carrier === Carrier.UPS && !config.userOwnedAccount
			);

		// show DHL when the ShowDHLPickup is true and DHL is configured
		const showDhlOption =
			ShowDhlPickup &&
			configuredCarriers.some(
				config =>
					config.carrier === Carrier.DHLExpress && !config.userOwnedAccount
			);

		const schedulePickupOptions = [
			{
				icon: `icon-${showUspsAndGlobalOption ? 'uspsgp' : 'usps'}`,
				label: showUspsAndGlobalOption ? 'USPS & GlobalPost' : 'USPS',
				command: this.onClickScheduleUspsPickupButton,
			},
			showUpsOption
				? {
						icon: 'icon-ups',
						label: 'UPS',
						command: this.onClickScheduleUpsPickupButton,
					}
				: null,
			showDhlOption
				? {
						icon: 'icon-dhlexpress',
						label: 'DHL Express',
						command: this.onClickScheduleDhlExpressPickupButton,
					}
				: null,
		].filter(option => option);

		this.state = {
			schedulePickupOptions,
			enableReprintLastLabelMenuCallback: !!localStorage.getItem(
				`reprint_container_label_ids_${userId}`
			),
		};

		this.sessionSubscriberIndex = undefined;
	}

	componentDidMount() {
		const { headerTrigger } = this.props;

		if (headerTrigger === EligibleForParams.Pickup) {
			this.renderManagePickupsModal();
		}

		// Subscribe to Session changes to update UI when container label count changes
		this.sessionSubscriberIndex = Session.subscribe(changeType => {
			if (changeType === 'containerLabelCount' || changeType === 'all') {
				this.forceUpdate();
			}
		});
	}

	componentWillUnmount() {
		// Clean up subscription to prevent memory leaks
		if (this.sessionSubscriberIndex !== undefined) {
			Session.unsubscribe(this.sessionSubscriberIndex);
		}
	}

	/**
	 * enable "Reprint Last Label" menu after user printed any container label(s).
	 *
	 */
	enableReprintLastLabelMenuCallback = () => {
		const accountInfo = Session.getAccountInfo();
		const { userId } = accountInfo;

		this.setState({
			// enabled the menu when container labels exist in the localstorage
			enableReprintLastLabelMenuCallback: !!localStorage.getItem(
				`reprint_container_label_ids_${userId}`
			),
		});
	};

	/**
	 * show UPS/DHL Express carrier pickup modal
	 * @param {Carrier} carrier
	 */
	showScheduleCarrierPickup = async carrier => {
		const { closeAll, showModal, showError } = this.context;
		const { fetchPrints } = this.props;
		const { prints = [] } = await fetchPrints({
			carriers: [carrier],
			eligible_for_carrier_pickup: true,
		});

		if (prints.length <= 0) {
			return showError({
				data: {
					title: 'Pickup Error',
					description: `
						<p>
							No prints are eligible for pickup.
						</p>
					`,
					primaryButtonText: 'Close',
					handleType: 'custom',
				},
			});
		}

		// Close all the existing modals before open ScheduleCarrierPickupModal.
		closeAll(() => {
			showModal({
				component: ScheduleCarrierPickupModal,
				data: {
					prints,
					onClickManagePickupsModalButton: this.onClickManagePickupsModalButton,
				},
			});
		});
	};

	/**
	 * Return the cost code id of all selected prints. If selected prints
	 * have different cost code association, returns `-1`; otherwise
	 * returns the cost code id.
	 *
	 * @param {array} selectedPrints The selected prints in the grid.
	 * @returns {number}
	 */
	getSelectedCostCodeId = selectedPrints => {
		if (isArray(selectedPrints) && selectedPrints.length > 0) {
			const firstSelectedPrint = selectedPrints[0];
			const firstCostCodeId = isNumber(
				firstSelectedPrint.printDetails?.costCodeId
			)
				? firstSelectedPrint.printDetails?.costCodeId
				: -1;

			for (let idx = 1; idx < selectedPrints.length; idx++) {
				const print = selectedPrints[idx];

				if (print.printDetails?.costCodeId !== firstCostCodeId) {
					return 0; // 'None' option
				}
			}

			return firstCostCodeId;
		}

		return -1;
	};

	// show feedback modal
	onClickFeedbackButton = () => {
		const { showModal } = this.context;

		launchChatbot({
			flags: this.props.flags,
			showModal,
			trigger: ChatbotTriggers.FeedbackButton,
		});
	};

	// show "Change Cost Code" modal
	onClickChangeCostCode = () => {
		const { showModal } = this.context;
		const { costCodeOptions, selectedPrints, setPrintsCostCode } = this.props;

		if (selectedPrints.length > 0) {
			showModal({
				component: ChangeCostCodeModal,
				data: {
					costCodeOptions,
					setPrintsCostCode,

					// find the selected cost code from selected prints
					selectedCostCodeId: this.getSelectedCostCodeId(selectedPrints),
				},
			});
		}
	};

	// show Refund modal
	onClickRefundButton = () => {
		const { showModal } = this.context;
		const { selectedPrints, onRefreshGridAPI } = this.props;
		const eligiblePrints = selectedPrints.filter(
			({ printDetails }) => printDetails?.eligibleForRefund === true
		);
		const totalIneligiblePrints = selectedPrints.length - eligiblePrints.length;
		const netstampsPrints = selectedPrints.filter(
			({ printDetails }) => printDetails?.printTypeName === 'NetStamps'
		);

		// only one netstamps is allowed, this is a workaround WEBAPPS-38520
		if (netstampsPrints.length > 1) {
			const { showMessage } = this.context;

			return showMessage({
				data: {
					title: 'Too Many Selections',
					description: `
						<p>
							Refund requests can only include one row of NetStamps prints at a time. Please update your selection to include just one NetStamps row and try again.
						</p>
					`,
					primaryButtonText: 'Close',
				},
			});
		}

		// Refund Assist prints are ineligible for refund
		else if (
			eligiblePrints.length > 0 &&
			selectedPrints.some(
				item =>
					item.printDetails &&
					item.printDetails.refundStatus === 'Request Scheduled'
			)
		) {
			showModal({
				component: PartialEligibilityWarningModal,
				data: {
					totalIneligiblePrints,
					totalSelectedPrints: selectedPrints.length,
					eligiblePrints,
					onClickLearnMore: this.onClickLearnMore,
					onRefreshGridAPI,
					message: (
						<>
							<p>
								Items with the refund type Refund Assist cannot be refunded
								manually.
							</p>
							<p>
								By clicking Continue, only eligible labels will be refunded and
								all others will be ignored.
							</p>
						</>
					),
				},
			});
		}

		// All selected prints are eligible
		else if (totalIneligiblePrints === 0) {
			showModal({
				component: RefundModal,
				data: {
					eligiblePrints,
					onRefreshGridAPI,
				},
			});
		}

		// Only some of the selected prints are eligible
		else if (totalIneligiblePrints > 0 && eligiblePrints.length > 0) {
			showModal({
				component: PartialEligibilityWarningModal,
				data: {
					totalIneligiblePrints,
					totalSelectedPrints: selectedPrints.length,
					eligiblePrints,
					onClickLearnMore: this.onClickLearnMoreForRefund,
					onRefreshGridAPI,
				},
			});
		}

		// No selected prints are eligible
		else if (totalIneligiblePrints === selectedPrints.length) {
			showModal({
				component: ErrorRefundModal,
				data: {
					onClickLearnMore: this.onClickLearnMoreForRefund,
				},
			});
		}
	};

	onClickLearnMoreForRefund = () => {
		const { selectedPrints } = this.props;

		const netstampsPrints = selectedPrints.filter(
			({ printDetails }) => printDetails?.printTypeName === 'NetStamps'
		);

		const envelopePrints = selectedPrints.filter(
			({ printDetails }) => printDetails?.printTypeName === 'Envelope'
		);

		if (netstampsPrints.length > 0) {
			Util.openHelpCenter('20821630250523');
		} else if (envelopePrints.length > 0) {
			Util.openHelpCenter('20821666984987');
		} else {
			Util.openHelpCenter('20821667404827');
		}
	};

	onClickLearnMore = () => {
		const helpCenterNumber = `21040486749723'#UUID-fb43d801-ab46-a23a-db58-216f7a60b5b2_UUID-72dcae43-fe44-d48b-cc8f-c7f7d6948e26'`;

		Util.openHelpCenter(helpCenterNumber);
	};

	onClickSchedulePickupLearnMore = () => {
		const helpCenterNumber = '21040486749723-Schedule-a-Pickup';

		Util.openHelpCenter(helpCenterNumber);
	};

	onClickScheduleUpsPickupButton = () => {
		this.showScheduleCarrierPickup(Carrier.UPS);
	};

	onClickScheduleDhlExpressPickupButton = async () => {
		this.showScheduleCarrierPickup(Carrier.DHLExpress);
	};

	// show Schedule USPS Pickup Modal
	onClickScheduleUspsPickupButton = async () => {
		const { closeAll, showModal } = this.context;
		const { fetchPrints, selectedPrints } = this.props;

		const { prints = [] } = await fetchPrints({
			carriers: [Carrier.USPS, Carrier.GlobalPost],
			eligible_for_carrier_pickup: true,
		});

		if (prints.length === 0) {
			return showModal({
				component: PickupErrorModal,
				data: {
					onClickLearnMore: this.onClickSchedulePickupLearnMore,
				},
			});
		}

		// Close all the existing modals before open ScheduleUspsPickupModal.
		// This will be reused in ManagePickupsModal component.
		closeAll(() => {
			showModal({
				component: ScheduleUspsPickupModal,
				data: {
					selectedPrints,
					fetchPrints,
				},
			});
		});
	};

	onClickManagePickupsModalButton = () => {
		this.renderManagePickupsModal();
	};

	fetchPickups = async () => {
		const { showError } = this.context;
		const response = await HistoryCommunication.getSchedulePickup();

		if (HistoryCommunication.isSuccessful(response)) {
			const pickupsResponse = Util.toCamelCase(response.data);
			return isArray(pickupsResponse?.pickups)
				? pickupsResponse
				: { pickups: [] };
		} else {
			showError({ response });
			return { pickups: [] };
		}
	};

	renderManagePickupsModal = async () => {
		const { closeAll, showModal } = this.context;
		const { schedulePickupOptions } = this.state;
		const { onRefreshGridAPI } = this.props;
		const { pickups } = (await this.fetchPickups()) || {};

		closeAll(() => {
			showModal({
				component: ManagePickupsModal,
				data: {
					pickups,
					schedulePickupOptions,
					onClickManagePickupsModalButton: this.renderManagePickupsModal,
					fetchPickups: this.fetchPickups,
					onRefreshGridAPI,
				},
			});
		});
	};

	onClickAllEligiblePackagesForContainerLabel = async () => {
		const { showModal, showError } = this.context;
		const { prints, onRefreshGridAPI } = this.props;

		const eligiblePrints = prints.filter(
			({ printDetails }) => printDetails?.eligibleForContainerLabel === true
		);

		// none eligible prints
		if (eligiblePrints.length === 0) {
			return showError({
				data: {
					handleType: 'custom',
					title: 'Error',
					description:
						'You need an eligible package to print a Container Label.',
				},
			});
		}

		showModal({
			component: CreateContainerLabelModal,
			data: {
				prints: eligiblePrints,
				enableReprintLastLabelMenuCallback:
					this.enableReprintLastLabelMenuCallback,
				onRefreshGridAPI,
			},
		});
	};

	onClickSelectedPackagesForContainerLabel = () => {
		const { showModal, showError } = this.context;
		const { selectedPrints, onRefreshGridAPI } = this.props;
		const eligiblePrints = selectedPrints.filter(
			({ printDetails }) => printDetails?.eligibleForContainerLabel === true
		);

		// none eligible prints
		if (eligiblePrints.length === 0) {
			return showError({
				data: {
					handleType: 'custom',
					title: 'Error',
					description:
						'You need an eligible package to print a Container Label.',
				},
			});
		}
		// some ineligible prints
		else if (eligiblePrints.length !== selectedPrints.length) {
			showError({
				data: {
					handleType: 'custom',
					title: 'Container Label Warning',
					description: `${eligiblePrints.length} of the ${selectedPrints.length} prints are not eligible for a Container Label. To proceed with only the eligible prints, click Continue.`,
					primaryButtonType: WrappedButtonType.Primary,
					primaryButtonText: 'Continue',
				},
				closeCallback: buttonType => {
					if (buttonType === WrappedButtonType.Primary) {
						showModal({
							component: CreateContainerLabelModal,
							data: {
								prints: eligiblePrints,
								enableReprintLastLabelMenuCallback:
									this.enableReprintLastLabelMenuCallback,
								onRefreshGridAPI,
							},
						});
					}
				},
			});
		} else {
			// all selected prints are eligible
			showModal({
				component: CreateContainerLabelModal,
				data: {
					prints: eligiblePrints,
					enableReprintLastLabelMenuCallback:
						this.enableReprintLastLabelMenuCallback,
					onRefreshGridAPI,
				},
			});
		}
	};

	// Todo: system makes a call to GetOriginZipforScanForm API to get all eligible prints and show SCAN Form modal
	onClickAllEligiblePackagesScanForm = () => {
		const { showModal } = this.context;
		const { fetchPrints, onRefreshGridAPI } = this.props;

		// Update when Logic is implemented to provide all eligiblePrints
		showModal({
			component: CreateScanFormModal,
			data: {
				isForAllEligible: true,
				fetchPrints,
				onRefreshGridAPI,
			},
		});
	};

	onClickSelectedPackagesScanForm = () => {
		const { showModal } = this.context;
		const { selectedPrints, onRefreshGridAPI } = this.props;

		if (selectedPrints.length > 0) {
			showModal({
				component: CreateScanFormModal,
				data: {
					selectedPrints,
					isForAllEligible: false,
					onRefreshGridAPI,
				},
			});
		}
	};

	// TODO: System makes a call to Load SCAN Form data API to load the data in the grid.
	onClickReprintScanForm = () => {
		const { showModal } = this.context;

		showModal({
			component: ReprintScanFormModal,
		});
	};

	onClickAllEligiblePackagesDHLeC = () => {
		const { showModal } = this.context;
		const { fetchPrints } = this.props;

		showModal({
			component: CreateDHLeCModal,
			data: {
				isForAllEligible: true,
				fetchPrints,
			},
		});
	};

	onClickReprintDHLeC = () => {
		const { showModal } = this.context;

		showModal({
			component: ReprintDHLeCModal,
		});
	};

	/**
	 * check prints are eligibleForContainerLabel is true
	 * @returns {bool}
	 */
	eligibleContainerLabelsCount = () => {
		const {
			dynamicSettings: { containerLabelCount },
		} = Session.getData();

		return containerLabelCount;
	};

	eligibleRefundAssistLabelsCount = () => {
		const {
			dynamicSettings: { refundAssistLabelCount },
		} = Session.getData();

		return refundAssistLabelCount;
	};

	onClickCreateReturnLabel = () => {
		const { onClickCreateReturnLabelButton } = this.props;

		onClickCreateReturnLabelButton('historyPageToolbar');
	};

	showUpgradeModalToCore = () => {
		const { showModal } = this.context;
		const upgradeToCorePlan = BillingPlans.getPlanByPlanId(PlanIds.CoreV380);
		const { selectedPrints } = this.props;
		const upgradePrompt = 'upgradePromptSchedulePickup';
		const schedulePickupSegmentTracking = userAction =>
			Analytics.trackEvent(
				upgradePrompt,
				{
					pointOfOrigin: 'historyPageToolbar',
					userAction,
					printsSelected: selectedPrints ? selectedPrints.length : null,
				},
				Analytics.agent.segment
			);

		// show upgrade plan modal to core plan
		showModal({
			component: UpgradePlanModal,
			data: {
				upgradePlan: upgradeToCorePlan,
				upgradePrompt,
				segmentTrackingCallback: schedulePickupSegmentTracking,
			},
			closeCallback: closeType => {
				if (closeType === ModalButtonActionType.Ok) {
					schedulePickupSegmentTracking('closeModal');
				}
			},
		});
	};

	createRefundAssistOptOutPayload = selectedPrints => {
		const refundAssistOptOuts = [];

		selectedPrints.forEach(selectedPrint => {
			refundAssistOptOuts.push({
				confirmation_number: selectedPrint?.printDetails?.trackingNumber,
				universal_id: selectedPrint?.printDetails?.universalId,
				date_created: selectedPrint?.transactionDate,
				is_auto_refund_opt_out: true,
			});
		});

		const payload = {
			cust_id: selectedPrints[0].custId,
			auto_refund_opt_outs: refundAssistOptOuts,
		};

		return payload;
	};

	onClickExcludeRefundAssit = async () => {
		const { onRefreshGridAPI, setGridLoader, selectedPrints } = this.props;
		const { showModal, showError, closeAll } = this.context;
		const eligiblePrints = selectedPrints.filter(
			({ printDetails }) => printDetails?.refundStatus === 'Request Scheduled'
		);
		const totalIneligiblePrints = selectedPrints.length - eligiblePrints.length;
		const continueExcludeRefundAssist = async () => {
			closeAll();
			const response = await HistoryCommunication.excludeAutorefundAssist(
				this.createRefundAssistOptOutPayload(eligiblePrints)
			);

			if (HistoryCommunication.isSuccessful(response)) {
				// Due to the API response dealy, we are adding a delay to show the loader
				setTimeout(() => {
					onRefreshGridAPI();
					setGridLoader(false);
				}, 1000);
			} else {
				setGridLoader(false);
				showError({ response });
			}
		};

		setGridLoader(true);

		// Refund Assist prints are ineligible for refund
		if (
			eligiblePrints.length > 0 &&
			selectedPrints.length - eligiblePrints.length > 0
		) {
			showModal({
				component: PartialEligibilityWarningModal,
				data: {
					totalIneligiblePrints,
					totalSelectedPrints: selectedPrints.length,
					eligiblePrints,
					onClickLearnMore: this.onClickLearnMore,
					onRefreshGridAPI,
					onContinueClick: continueExcludeRefundAssist,
					message: (
						<>
							<p>
								Only items with the refund type Refund Assist can be excluded
								from Refund Assist.
							</p>
							<p>Click Continue to exclude these labels from Refund Assist.</p>
							<p>All other selected labels will be ignored.</p>
						</>
					),
				},
			});
		} else {
			continueExcludeRefundAssist();
		}
	};

	renderRefundButtonModel = () => {
		const { selectedPrints, activeMenuItem = {} } = this.props;
		const { activeMenuItemValue } = activeMenuItem;
		const selectedPrintsContainEligibleForRefundAssist = selectedPrints.some(
			({ printDetails }) => printDetails?.eligibleForRefundAssist === true
		);

		const refundModel = [
			{
				label: 'Exclude from Refund Assist',
				disabled:
					selectedPrints.length === 0 ||
					!selectedPrintsContainEligibleForRefundAssist,
				command: () => {
					this.onClickExcludeRefundAssit();
				},
			},
		];

		// do not show 'Request a refund' button when the Refund Assist filter is active
		if (activeMenuItemValue !== EligibleFor.RefundAssist) {
			refundModel.unshift({
				label: 'Request a refund',
				disabled: selectedPrints.length === 0,
				command: () => {
					this.onClickRefundButton();
				},
			});
		}

		return Session.hasCapability(Capability.RefundAssistEnabled)
			? refundModel
			: null;
	};

	render() {
		const { selectedPrints, onClickManageCostCodes, handleExportToCsv } =
			this.props;
		const { schedulePickupOptions } = this.state;
		const { globalpost = {} } = Session.getAccountInfo();
		const { firstMileCarrier = '' } = globalpost;
		const containerLabelCount = this.eligibleContainerLabelsCount();
		const _refundAssistLabelCount = this.eligibleRefundAssistLabelsCount();
		const canPrintReturnLabel = Session.hasCapability(
			Capability.HistoryReturnLabel
		);
		const canUseSchedulePickup = Session.hasCapability(
			Capability.CanUseSchedulePickup
		);

		const costCodesUpgradeProps = getUpgradeIconProps('costCodes', {
			showModal: this.context.showModal,
		});

		const buttons = [
			{
				label: 'Refund',
				className: 'refund-toolbar-button',
				icon: null,
				dataqa: 'refund',
				disabled: selectedPrints.length === 0,
				tooltip: !Session.hasCapability(Capability.RefundAssistEnabled)
					? `<b>Refund</b><br />Request a refund for your unused prints.`
					: '',
				tooltipOptions: {
					position: 'bottom',
				},
				onClick: Session.hasCapability(Capability.RefundAssistEnabled)
					? null
					: this.onClickRefundButton,
				model: this.renderRefundButtonModel(),
			},
			canUseSchedulePickup
				? {
						label: 'Schedule Pickup',
						menuClassName: 'schedule-pickup-split-button-menu',
						dataqa: 'schedule-pickup',
						disabled: false,
						model: [
							...schedulePickupOptions,
							{
								label: 'Manage Pickups',
								command: () => {
									this.onClickManagePickupsModalButton();
								},
							},
						],
						tooltip: `<b>Schedule Pickup</b><br />Schedule a carrier pickup for your selected prints.`,
						tooltipOptions: {
							position: 'bottom',
						},
					}
				: {
						label: 'Schedule Pickup',
						icon: 'upgrade-icon',
						className: 'upgrade-icon-btn',
						dataqa: 'schedule-pickup',
						disabled: false,
						tooltip: `<b>Schedule Pickup</b><br />Upgrade your plan to Core to schedule a pickup.`,
						tooltipOptions: {
							position: 'bottom',
						},
						onClick: this.showUpgradeModalToCore,
					},
			Session.hasCapability(Capability.Scanform)
				? {
						label: 'Create SCAN Form',
						dataqa: 'create-scan-form-button',
						disabled: false,
						buttonType: WrappedButtonType.TertiaryGhost,
						model: [
							{
								label: 'All Eligible Packages',
								command: this.onClickAllEligiblePackagesScanForm,
								dataqa: 'create-scan-form-all-eligible-packages',
							},
							{
								label: 'Selected Packages',
								disabled: selectedPrints.length === 0,
								command: this.onClickSelectedPackagesScanForm,
								dataqa: 'create-scan-form-selected-packages',
							},
							{
								label: 'Reprint SCAN Form',
								command: this.onClickReprintScanForm,
								dataqa: 'create-scan-form-reprint-scan-form',
							},
						],
						tooltip: `<b>SCAN Form</b><br />Create a SCAN Form for your selected prints.`,
						tooltipOptions: {
							position: 'bottom',
						},
					}
				: null,
			// show this button only when user has the DHL eCommerce capability
			Session.hasCapability(Capability.DhlEcommerce)
				? {
						label: 'Create DHL eCommerce Manifest',
						dataqa: 'create-dhlec-manifest',
						disabled: false,
						buttonType: WrappedButtonType.TertiaryGhost,
						model: [
							{
								label: 'All Eligible Packages',
								command: this.onClickAllEligiblePackagesDHLeC,
								dataqa: 'create-dhl-manifest-all-eligible-packages',
							},
							{
								label: 'Reprint DHLeC Manifest',
								command: this.onClickReprintDHLeC,
								dataqa: 'create-dhl-manifest-reprint-manifest',
							},
						],
						tooltip: `<b>DHL eCommerce Manifest</b><br />Create a DHL eCommerce Manifest for eligible prints.`,
						tooltipOptions: {
							position: 'bottom',
						},
					}
				: null,
			{
				label: 'Create Return Label',
				icon: canPrintReturnLabel ? null : 'upgrade-icon',
				className: canPrintReturnLabel
					? 'create-return-label-button'
					: 'create-return-label-button upgrade-icon-btn',
				dataqa: 'create-return-label',
				disabled: false,
				buttonType: WrappedButtonType.TertiaryGhost,
				tooltip: canPrintReturnLabel
					? `<b>Create Return Label</b><br/>Create a return label for the selected print, or create a stand-alone return label if no prints are selected.`
					: 'Upgrade your plan to Office to Create Return Label.',
				tooltipOptions: {
					position: 'bottom',
				},
				onClick: this.onClickCreateReturnLabel,
			},
			// show this button only when user’s capability "CanUseCodeCodes" is true
			Session.hasCapability(Capability.CostCodes)
				? {
						label: 'Cost Codes',
						dataqa: 'cost-codes',
						buttonType: WrappedButtonType.TertiaryGhost,
						disabled: false,
						...costCodesUpgradeProps,
						model: Session.hasCapability(InternalCapability.EditCostCodes)
							? [
									{
										disabled: selectedPrints.length === 0,
										label: 'Change Cost Code',
										command: this.onClickChangeCostCode,
										dataqa: 'cost-codes-change-cost-code',
									},
									{
										label: 'Add/Edit Cost Codes',
										command: onClickManageCostCodes,
										dataqa: 'cost-codes-add-edit-cost-code',
									},
								]
							: [
									{
										disabled: selectedPrints.length === 0,
										label: 'Change Cost Code',
										command: this.onClickChangeCostCode,
										dataqa: 'cost-codes-change-cost-code',
									},
								],
						tooltip: `<b>Cost Code</b><br />Change the Cost Code assigned to the selected prints.`,
						tooltipOptions: {
							position: 'bottom',
						},
					}
				: null,
			// show this button only when user can create container label
			firstMileCarrier === Carrier.USPS
				? {
						label: 'Create Container Label',
						dataqa: 'create-container-label',
						disabled: false,
						buttonType: WrappedButtonType.TertiaryGhost,
						showBadge: containerLabelCount > 0,
						badgeCount:
							containerLabelCount <= 99 ? `${containerLabelCount}` : '99+',
						model: [
							{
								label: 'All Eligible Packages',
								command: this.onClickAllEligiblePackagesForContainerLabel,
								dataqa: 'create-container-label-all-eligible-packages',
							},
							{
								label: 'Selected Packages',
								disabled: selectedPrints.length === 0,
								command: () => {
									if (selectedPrints.length > 0) {
										this.onClickSelectedPackagesForContainerLabel();
									}
								},
								dataqa: 'create-container-label-selected-packages',
							},
						],
						tooltip: `<b>Create Container Label</b><br />Create Container Labels for SmartSaver packages that will be placed in large containers and sent to the Global Advantage Processing Facility. <br /><br />${
							containerLabelCount || 0
						} package(s) require a container label.`,
						tooltipOptions: {
							position: 'bottom',
						},
					}
				: null,
			{
				icon: 'export-icon',
				buttonType: WrappedButtonType.TertiaryWhite,
				label: 'Export',
				dataqa: 'export',
				group: 'right',
				onClick: handleExportToCsv,
				tooltip: `<b>Export</b><br />Export your history into a CSV file.`,
				tooltipOptions: {
					position: 'bottom',
				},
			},
			{
				icon: 'feedback-icon',
				buttonType: WrappedButtonType.TertiaryWhite,
				label: 'Feedback',
				dataqa: 'feedback',
				group: 'right',
				onClick: this.onClickFeedbackButton,
				tooltip: "<b>Feedback</b>\nTell us how we're doing!",
				tooltipOptions: {
					position: 'bottom',
				},
			},
		].filter(button => button);

		return (
			<>
				<WrappedToolbar
					className="history history-toolbar"
					buttons={buttons}
					dataqa="history-toolbar"
				/>
			</>
		);
	}
}

Toolbar.contextType = ModalContext;

Toolbar.propTypes = {
	selectedPrints: PropTypes.array,

	costCodeOptions: PropTypes.array,

	fetchPrints: PropTypes.func,

	// callback to set cost code to the selected prints.
	setPrintsCostCode: PropTypes.func,
};

export default withLDConsumer()(Toolbar);
